import { z } from "zod";
import { PerfFlagSchema } from "./perf";

/**
 * The daily health loop's view of the last day (docs/health.md): `GET /v1/admin/health/digest`
 * groups what went wrong and where players struggled, from `game_events`, so the feedback bot can
 * ask a model for improvements sorted by impact. Character names only; bounded.
 */

const Quant = z.object({
  n: z.number(),
  median: z.number().nullable(),
  p90: z.number().nullable(),
});

export const HealthDigestSchema = z.object({
  since: z.string(),
  until: z.string(),
  window: z.object({
    events: z.number(),
    truncated: z.boolean(),
    oldestKept: z.string().nullable(),
  }),
  /** Server starts in the window (a deploy or a restart), with the build they ran. */
  deploys: z.array(
    z.object({ at: z.string(), version: z.string(), commit: z.string().nullable() }),
  ),
  players: z.object({
    active: z.number(),
    sessions: z.number(),
    /** Minutes per session (socket attach to detach). */
    minutes: Quant,
    /** Sessions shorter than 2 minutes: bounced. */
    short: z.number(),
  }),
  /** Grouped by where and what, most frequent first. */
  errors: z.array(
    z.object({
      source: z.enum(["server", "client", "game"]),
      key: z.string(),
      count: z.number(),
      first: z.string(),
      last: z.string(),
      sample: z.string(),
    }),
  ),
  /** Refused intents by action and reason: the friction players hit, most frequent first. */
  rejects: z.array(
    z.object({
      action: z.string(),
      reason: z.string(),
      count: z.number(),
      players: z.number(),
      /** Times one player hit it 3+ times within a minute (trying again and again). */
      bursts: z.number(),
    }),
  ),
  /** Quests taken in the window and not finished in it, most abandoned first. */
  quests: z.array(
    z.object({ id: z.string(), name: z.string(), accepted: z.number(), completed: z.number() }),
  ),
  /** Panels opened and closed without doing anything in them (client session summaries). */
  panels: z.array(z.object({ panel: z.string(), opens: z.number(), empty: z.number() })),
  connections: z.object({
    attached: z.number(),
    resumed: z.number(),
    lingered: z.number(),
    kickedDuplicate: z.number(),
    resumeRefused: z.number(),
    /** Client-side reconnect attempts (session summaries). */
    clientReconnects: z.number(),
    mostResumed: z.array(z.object({ name: z.string(), count: z.number() })),
  }),
  slowRequests: z.array(
    z.object({
      method: z.string(),
      path: z.string(),
      count: z.number(),
      maxMs: z.number(),
      statuses: z.array(z.number()),
    }),
  ),
  ticks: z.object({
    minutesWithOverruns: z.number(),
    overruns: z.number(),
    worstMs: z.number(),
    /** Wall time no World ticked through (a starved or stalled host). */
    lostSeconds: z.number().default(0),
  }),
  /**
   * The server's per-minute samples (`server.perf`): each minute's figure, across minutes. Null
   * from a server older than them (a digest read across a deploy).
   */
  server: z
    .object({
      minutes: z.number(),
      tickP95: Quant,
      loopP99: Quant,
      cpuPct: Quant,
      stealPct: Quant,
      rssMbMax: z.number().nullable(),
    })
    .nullable()
    .default(null),
  /** What needs a look (the admin Health view's flags, `PERF_THRESHOLDS`). */
  flags: z.array(PerfFlagSchema).default([]),
  client: z.object({
    summaries: z.number(),
    byDevice: z.record(z.string(), z.number()),
    loadMs: Quant,
    /** Each session's median and 10th-percentile frame rate, summarized across sessions. */
    fpsMedian: Quant,
    fpsP10: Quant,
    longFramesPerMinute: z.number().nullable(),
    /** Each session's network round trip (ms) and snapshot gap p95 (ms), across sessions. */
    rttP50: Quant.nullable().default(null),
    rttP95: Quant.nullable().default(null),
    snapGapP95: Quant.nullable().default(null),
    /** Server corrections (snap-backs) per minute of visible play. */
    correctionsPerMinute: z.number().nullable().default(null),
  }),
});
export type HealthDigest = z.infer<typeof HealthDigestSchema>;
